import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Award, BookOpen, Star, TrendingUp, Gift, ArrowRight, BrainCircuit, Sparkles } from 'lucide-react';

import { useDashboard } from '../hooks/useDashboard';
import { Button } from '../components/ui/Button';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import type { Word } from '../types';
import { getLocalizedDifficultyLabel } from '../utils/difficultyUtils';
import { useTranslation } from 'react-i18next';
import { useProfile } from '../hooks/useProfile';


const StatCard = ({ title, value, icon, color, unit }: { title: string, value: string | number, icon: React.ReactNode, color: string, unit?: string }) => (
    <motion.div
        whileHover={{ scale: 1.05, y: -5 }}
        className={`bg-white rounded-3xl shadow-lg p-6 flex flex-col items-center justify-center text-center border-b-8 ${color}`}>
        <div className="text-5xl mb-3">{icon}</div>
        <p className="text-4xl font-bold text-gray-800">{value}<span className="text-2xl font-medium text-gray-500">{unit}</span></p>
        <p className="text-gray-500 text-lg font-medium mt-1">{title}</p>
    </motion.div>
);

const RecentProgressItem = ({ word, category, score }: { word: string, category: string, score: number }) => {
    const scoreColor = score > 80 ? 'text-green-500' : score > 60 ? 'text-yellow-500' : 'text-red-500';
    return (
        <motion.li
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center justify-between py-4 px-5 rounded-2xl bg-white hover:bg-purple-50 transition-colors duration-300 shadow-sm">
            <div>
                <p className="font-bold text-xl text-gray-800">{word}</p>
                <p className="text-md text-purple-600 font-semibold">{category}</p>
            </div>
            <div className="text-right">
                <p className={`font-bold text-2xl ${scoreColor}`}>{score}%</p>
                <p className="text-sm text-gray-500">Score</p>
            </div>
        </motion.li>
    );
};

const AchievementBadge = ({ name, icon, description }: { name: string, icon: string, description: string }) => (
    <motion.div
        whileHover={{ scale: 1.1, rotate: 5 }}
        className="bg-yellow-100 border-2 border-yellow-300 p-4 rounded-2xl text-center flex flex-col items-center justify-center h-full transform transition-transform duration-300">
        <p className="text-5xl mb-3">{icon}</p>
        <p className="font-bold text-yellow-800 text-lg">{name}</p>
        <p className="text-sm text-yellow-700 mt-1">{description}</p>
    </motion.div>
);

const WordCard = ({ word, title, icon, buttonText, to, gradient, t }: { word: Word, title: string, icon: React.ReactNode, buttonText: string, to: string, gradient: string, t: any }) => (
    <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className={`rounded-3xl shadow-xl p-8 flex flex-col justify-between text-white ${gradient}`}>
        <div>
            <div className="flex items-center space-x-4 mb-4">
                {icon}
                <h3 className="text-2xl font-bold">{title}</h3>
            </div>
            <h4 className="text-5xl font-extrabold tracking-tight drop-shadow-md">{word.word}</h4>
            <p className="mt-3 opacity-90">Category: <span className="font-semibold">{word.category}</span></p>
            <p className="opacity-90">Difficulty: <span className="font-semibold">{getLocalizedDifficultyLabel(word.difficulty_level, t)}</span></p>
        </div>
        <Link to={to} className="mt-8">
            <Button variant="secondary" className="w-full rounded-full py-4 text-xl font-bold bg-white/20 hover:bg-white/30 backdrop-blur-sm border-2 border-white/50">
                {buttonText} <ArrowRight className="w-6 h-6 ml-2" />
            </Button>
        </Link>
    </motion.div>
);

export default function DashboardPage() {
    const { data, error, isLoading } = useDashboard();
    const { t } = useTranslation();
    const { profile } = useProfile();

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-blue-50">
                <div className="text-center">
                    <LoadingSpinner size="lg" className="mb-4" />
                    <p className="text-lg text-blue-700 font-semibold">Loading Your Dashboard...</p>
                </div>
            </div>
        );
    }

    if (error) {
        console.log(error)
        return (
            <div className="min-h-screen bg-red-50 flex items-center justify-center">
                <div className="text-center max-w-md mx-auto p-8 bg-white rounded-2xl shadow-xl">
                    <h2 className="text-2xl font-bold text-red-700 mb-2">Oops! Something went wrong.</h2>
                    <p className="text-gray-600 mb-6">We couldn't load your dashboard. Please try again!</p>
                    <Button onClick={() => window.location.reload()} variant="secondary">Try Again</Button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-blue-100 to-purple-100 font-sans p-4 sm:p-6 lg:p-8">
            <div className="max-w-7xl mx-auto">
                <header className="mb-12 text-center">
                    <motion.h1
                        initial={{ y: -50, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ type: 'spring', stiffness: 100 }}
                        className="text-6xl font-extrabold text-purple-800 tracking-tight drop-shadow-lg">Hi, {profile?.display_name || 'friend'}! 👋</motion.h1>
                    <motion.p
                        initial={{ y: -30, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.2, type: 'spring' }}
                        className="text-gray-600 mt-3 text-2xl">Ready for a new adventure in learning?</motion.p>
                </header>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
                    <StatCard title="Words Practiced" value={data?.stats.totalWords ?? 0} icon={<BookOpen className="text-blue-500" />} color="border-blue-400" />
                    <StatCard title="Average Score" value={data?.stats.averageScore ?? 0} unit="%" icon={<Star className="text-green-500" />} color="border-green-400" />
                    <StatCard title="Practice Streak" value={data?.stats.streakDays ?? 0} unit="days" icon={<TrendingUp className="text-yellow-500" />} color="border-yellow-400" />
                    <StatCard title="Achievements" value={data?.unlockedAchievements.length ?? 0} icon={<Award className="text-red-500" />} color="border-red-400" />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-2 space-y-8">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            {data?.wordOfTheDay && (
                                <WordCard
                                    word={data.wordOfTheDay}
                                    title="Word of the Day"
                                    icon={<Gift className="w-8 h-8" />}
                                    buttonText="Practice Now"
                                    to={`/word/${data.wordOfTheDay.id}`}
                                    gradient="bg-gradient-to-br from-red-500 to-orange-500"
                                    t={t}
                                />
                            )}
                            {data?.learningRecommendations?.[0] && (
                                <WordCard
                                    word={data.learningRecommendations[0]}
                                    title="Recommended for You"
                                    icon={<BrainCircuit className="w-8 h-8" />}
                                    buttonText="Let's Try It"
                                    to={`/word/${data.learningRecommendations[0].id}`}
                                    gradient="bg-gradient-to-br from-blue-500 to-teal-400"
                                    t={t}
                                />
                            )}
                        </div>

                        <div className="bg-white/60 backdrop-blur-lg p-6 rounded-3xl shadow-lg">
                            <h2 className="text-3xl font-bold mb-5 text-gray-800 text-center">Your Recent Progress</h2>
                            {data?.recentProgress && data.recentProgress.length > 0 ? (
                                <ul className="space-y-3">
                                    {data.recentProgress.map(p => (
                                        <RecentProgressItem key={p.id} word={p.words.word} category={p.words.category || 'Unknown'} score={p.best_score ?? 0} />
                                    ))}
                                </ul>
                            ) : (
                                <div className="text-center py-10">
                                    <Sparkles className="mx-auto h-20 w-20 text-gray-400 mb-4" />
                                    <p className="text-gray-600 text-lg">No recent progress yet. Time to start learning!</p>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="bg-white/60 backdrop-blur-lg p-6 rounded-3xl shadow-lg">
                        <h2 className="text-3xl font-bold mb-5 text-gray-800 text-center">Latest Achievements</h2>
                        {data?.unlockedAchievements && data.unlockedAchievements.length > 0 ? (
                            <div className="grid grid-cols-2 gap-5">
                                {data.unlockedAchievements.map(ach => (
                                    <AchievementBadge
                                        key={ach.id}
                                        name={ach.achievement_details.name}
                                        icon={ach.achievement_details.icon}
                                        description={ach.achievement_details.description}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-16">
                                <Award className="mx-auto h-20 w-20 text-gray-400 mb-4" />
                                <p className="text-gray-600 text-lg">No achievements unlocked yet. Keep practicing!</p>
                            </div>
                        )}
                        <Link to="/profile#achievements" className="block mt-6">
                            <Button variant="secondary" className="w-full rounded-full py-3 text-lg font-semibold">View All Achievements</Button>
                        </Link>
                    </div>
                </div>

                <footer className="text-center mt-16">
                    <Link to="/words">
                        <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                            <Button size="lg" className="rounded-full px-12 py-4 text-2xl font-bold shadow-xl bg-purple-600 hover:bg-purple-700 transition-all transform hover:-translate-y-1">
                                Explore All Words
                            </Button>
                        </motion.div>
                    </Link>
                </footer>
            </div>
        </div>
    );
}